import { environment } from 'environments/environment';
import { deriveCodeChallenge, generateCodeVerifier, generateState } from './pkce';

/** `sessionStorage` key of the authorization request awaiting its `/auth/callback`. */
export const AUTHORIZATION_REQUEST_KEY = 'authorization-request';

/** What the callback needs to redeem the code: the CSRF `state`, the PKCE secret and the landing. */
export interface PendingAuthorizationRequest {
  state: string;
  codeVerifier: string;
  redirectURL: string;
}

/** The SPA's own redirect target; `/callback` stays reserved for satellites. */
export const authorizationRedirectUri = (): string => `${window.location.origin}/auth/callback`;

export function readPendingAuthorizationRequest(): PendingAuthorizationRequest | null {
  try {
    const raw = sessionStorage.getItem(AUTHORIZATION_REQUEST_KEY);
    return raw ? (JSON.parse(raw) as PendingAuthorizationRequest) : null;
  } catch {
    return null;
  }
}

export function clearPendingAuthorizationRequest(): void {
  sessionStorage.removeItem(AUTHORIZATION_REQUEST_KEY);
}

/**
 * Starts an authorization code + PKCE request: stores the pending request, then navigates the
 * browser to `/o-auth/authorize`. The backend answers with a code (live hub session) or sends the
 * user to the sign-in form with a `continue` back to this same request.
 */
export async function startAuthorizationRequest(redirectURL: string): Promise<void> {
  const request: PendingAuthorizationRequest = {
    state: generateState(),
    codeVerifier: generateCodeVerifier(),
    redirectURL,
  };
  sessionStorage.setItem(AUTHORIZATION_REQUEST_KEY, JSON.stringify(request));

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: environment.oAuth.applicationCode,
    redirect_uri: authorizationRedirectUri(),
    state: request.state,
    code_challenge: await deriveCodeChallenge(request.codeVerifier),
    code_challenge_method: 'S256',
  });
  window.location.href = `${environment.api.rest}/o-auth/authorize?${params}`;
}
